import React from 'react'
import cx from 'classnames'
import { useDevice } from 'device'
import { Helmet } from 'react-helmet-async'
import { useAb } from 'hooks'
import { constants } from 'helpers'

import { HeroMediaBlock, Text, Video, type TextStyle } from 'components/dataDisplay'
import { Bone, WidthContainer, WidthContainerCompensator } from 'components/layout'
import StyledTimer from 'compositions/StyledTimer/StyledTimer'

import GetStartedButton from '../GetStartedButton/GetStartedButton'

import LinkWrapper from './components/LinkWrapper/LinkWrapper'

import messages from './messages'

import desktopImage from './images/desktop-hero.jpg?src-loader&size'
import mobileImage from './images/mobile-hero.jpg?src-loader&size'

import desktopVideo from './images/desktop.mp4'
import mobileVideo from './images/mobile.mp4'
import desktopPreview from './images/desktop-preview.png'
import mobilePreview from './images/mobile-preview.png'


export type HeroSectionProps = {
  offerType: SubscriptionModule.OfferType
  price: string
  priceWithDiscount: string
  extraShippingPrice: string
  discount: string
  customTexts?: {
    preTitle?: string | Intl.Message
    title: string | Intl.Message
    text?: string | Intl.Message
    note?: string | Intl.Message
    afterButtonText?: string | Intl.Message
  }
  customTextStyles?: {
    preTitle?: TextStyle
    title: TextStyle
    text?: TextStyle
    note?: TextStyle
    afterButtonText?: TextStyle
  }
  isFetching: boolean
  isTimerEnabled?: boolean
  startButtonMessage?: string | Intl.Message
  noteMessage?: string | Intl.Message
  withHeroLink?: boolean
}

const messagesByOffer: Record<SubscriptionModule.OfferType, { title: Intl.Message, text: Intl.Message, note?: Intl.Message }> = {
  freeTrial: messages.freeTrial,
  free2ndMonth: messages.freeMonth,
  freeProduct: messages.freeProduct,
  discount: messages.discount,
}


const HeroSection: React.FunctionComponent<HeroSectionProps> = (props) => {
  const {
    customTexts,
    customTextStyles,
    discount,
    extraShippingPrice,
    isFetching,
    offerType,
    price,
    priceWithDiscount,
    isTimerEnabled,
    withHeroLink,
    startButtonMessage,
  } = props

  const { isMobile, isDesktop } = useDevice()
  const isHomeAnimatedHeroEnabled = useAb(constants.abTests.homeAnimatedHero) === 'b'

  const heroImage = isMobile ? mobileImage : desktopImage
  const heroVideoPreview = isMobile ? mobilePreview : desktopPreview

  let preTitle
  let titleMessage
  let textMessage
  let noteMessage
  let afterButtonText

  if (customTexts) {
    preTitle = customTexts?.preTitle
    titleMessage = customTexts?.title
    textMessage = customTexts?.text
    noteMessage = customTexts?.note
    afterButtonText = customTexts?.afterButtonText
  }
  else {
    titleMessage = {
      ...messagesByOffer[offerType]?.title || messages.noDiscount.title,
      values: {
        discount,
        price,
      },
    }

    if (messagesByOffer[offerType]?.text) {
      textMessage = {
        ...messagesByOffer[offerType]?.text,
        values: {
          price,
          extraShippingPrice,
          priceWithDiscount: priceWithDiscount || price,
        },
      }
    }

    if (messagesByOffer[offerType]?.note) {
      noteMessage = {
        ...messagesByOffer[offerType]?.note,
        values: {
          priceWithDiscount: priceWithDiscount || price,
          withBr: !isTimerEnabled,
        },
      }
    }

    // TODO: if CRO-144 ab test wins
    // Improve and use the code of passing customTexts to Hero instead of separate custom note message
    if (props.noteMessage !== undefined) {
      noteMessage = props.noteMessage
    }
  }

  const preTitleStyle = customTextStyles?.preTitle ?? (isMobile ? 'p1' : 'sh3')
  const titleStyle = customTextStyles?.title ?? (isMobile ? 'h3' : 'h1')
  const textStyle = customTextStyles?.text ?? (isMobile ? 'p1' : 'sh3')
  const noteStyle = customTextStyles?.note ?? (isMobile ? 't1' : 'sh3')
  const afterButtonTextStyle = customTextStyles?.afterButtonText ?? 'sh6'

  // inline without zina to improve page load speed (LCP)
  const style = {
    background: isHomeAnimatedHeroEnabled ? '#e6e0d9' : `url(${heroImage.src})`,
    backgroundSize: 'cover',
    minHeight: isMobile ? '658rem' : '768rem',
  }

  const containerClassName = isMobile
    ? 'flex w-full bg-gold-30 pt-32 text-center'
    : 'flex w-full items-center justify-start bg-gold-30'


  return (
    <HeroMediaBlock className="relative overflow-hidden" data-testid="heroSection">
      <Helmet>
        <link rel="preload" href={isHomeAnimatedHeroEnabled ? heroVideoPreview : heroImage.src} as="image" />
      </Helmet>
      {
        isHomeAnimatedHeroEnabled && !isMobile && (
          <Video
            className="absolute right-0 h-full w-1/2"
            videoClassName="bg-[#e6e0d9] object-left"
            url={desktopVideo}
            preview={desktopPreview}
            type="mp4"
            autoplay
            muted
            playsinline
            loop
            aspect={0.94}
            withBackground={false}
          />
        )
      }
      <div className={containerClassName} style={style}>
        {
          isFetching ? (
            <WidthContainer className={isMobile ? null : 'w-full max-w-464'}>
              <Bone
                className={cx('w-full max-w-336 rounded', isMobile && 'mx-auto')}
                h={isMobile ? 20 : 40}
                bgColor="gray-70"
              />
              <Bone
                className={cx('mt-12 rounded', isMobile && 'mx-auto')}
                w={isMobile ? 200 : 400}
                h={isMobile ? 20 : 40}
                bgColor="gray-70"
              />
              <Bone
                className={cx(isMobile ? 'mx-auto mt-16' : 'mt-32')}
                w={isMobile ? 240 : 280}
                h={isMobile ? 38 : 56}
                bgColor="gray-70"
              />
            </WidthContainer>
          ) : (
            <WidthContainer ignore={isMobile} className={cx('w-full', isMobile && 'flex flex-col px-16')}>
              <LinkWrapper isLink={withHeroLink}>
                {
                  Boolean(preTitle) && (
                    <Text
                      className="mb-20 text-white"
                      message={preTitle}
                      style={preTitleStyle}
                      html
                    />
                  )
                }
                <Text
                  className="max-w-464"
                  message={titleMessage}
                  style={titleStyle}
                  html
                />
                {
                  Boolean(textMessage) && (
                    <Text
                      className={isMobile ? 'mt-8' : 'mt-24 max-w-464 pr-64'}
                      message={textMessage}
                      style={textStyle}
                      html
                    />
                  )
                }
                {
                  Boolean(noteMessage) && (
                    <Text
                      className={isMobile ? 'mt-8' : cx('mt-16 max-w-464 pr-64', isTimerEnabled ? 'whitespace-nowrap' : 'whitespace-pre-line')}
                      message={noteMessage}
                      style={noteStyle}
                      color={isHomeAnimatedHeroEnabled ? 'gold-50' : 'white'}
                      html
                    />
                  )
                }
                {
                  Boolean(offerType && isTimerEnabled) && (
                    <StyledTimer
                      className={noteMessage ? null : 'mt-16'}
                      titleColor={isHomeAnimatedHeroEnabled ? 'gold-50' : 'white'}
                      backgroundColor={isHomeAnimatedHeroEnabled ? 'white' : undefined}
                      size={56}
                      withTitle
                    />
                  )
                }
              </LinkWrapper>
              {
                isDesktop && (
                  <GetStartedButton
                    className={isTimerEnabled ? 'mt-32' : 'mt-56'}
                    size={56}
                    width={280}
                    title={startButtonMessage}
                    placement="Hero banner"
                  />
                )
              }
              {
                Boolean(afterButtonText) && (
                  <Text
                    className={isMobile ? 'mt-8' : 'mt-20'}
                    message={afterButtonText}
                    style={afterButtonTextStyle}
                    html
                  />
                )
              }
              {
                isHomeAnimatedHeroEnabled && isMobile && (
                  <WidthContainerCompensator className="relative mt-16 flex-1">
                    <div className="absolute top-0 left-0 z-10 h-64 w-full" style={{ background: 'linear-gradient(180deg, #e6e0d9 0%, rgba(230, 224, 217, 0) 100%)' }} />
                    <Video
                      className="h-full w-full"
                      videoClassName="bg-[#E4DBD3]"
                      url={mobileVideo}
                      preview={mobilePreview}
                      type="mp4"
                      autoplay
                      muted
                      playsinline
                      loop
                      aspect={1}
                      withBackground={false}
                    />
                  </WidthContainerCompensator>
                )
              }
            </WidthContainer>
          )
        }
      </div>
    </HeroMediaBlock>
  )
}


export default React.memo(HeroSection)
